import React, { useMemo, useState } from 'react';
import { FaBell, FaBullhorn, FaLock } from 'react-icons/fa';
import marketingImage from '../../../assets/images/marketing.png';
import utilityImage from '../../../assets/images/utility.png';
import authenticationImage from '../../../assets/images/auth.png';
import TemplateBuilder from '../components/builder/TemplateBuilder';
import '../styles/create-template.css';

const CATEGORY_CONFIG = {
  marketing: {
    key: 'marketing',
    label: 'Marketing',
    icon: FaBullhorn,
    image: marketingImage,
    options: [
      {
        value: 'custom',
        label: 'Custom',
        description: 'Send promotions or announcements to increase awareness and engagement.',
        type: 'builder',
      },
      {
        value: 'catalogue',
        label: 'Catalogue',
        description: 'Coming soon: trigger product catalogue based workflows.',
        type: 'info',
        message: 'Catalogue templates are coming soon.',
      },
      {
        value: 'flow',
        label: 'Flows',
        description: 'Coming soon: guide customers through conversational flows.',
        type: 'info',
        message: 'Flow templates are not available yet.',
      },
    ],
  },
  utility: {
    key: 'utility',
    label: 'Utility',
    icon: FaBell,
    image: utilityImage,
    options: [
      {
        value: 'custom',
        label: 'Custom',
        description: 'Send proactive updates about orders, accounts, or system alerts.',
        type: 'builder',
      },
    ],
  },
  authentication: {
    key: 'authentication',
    label: 'Authentication',
    icon: FaLock,
    image: authenticationImage,
    options: [
      {
        value: 'otp',
        label: 'One-time passcode',
        description: 'Send secure verification codes to authenticate users.',
        type: 'builder',
      },
    ],
  },
};

const CATEGORY_ORDER = ['marketing', 'utility', 'authentication'];

const CreateTemplate = () => {
  const [activeCategory, setActiveCategory] = useState('marketing');
  const [selectedByCategory, setSelectedByCategory] = useState({ marketing: 'custom', utility: 'custom', authentication: 'otp' });
  const [feedback, setFeedback] = useState('');
  const [builderConfig, setBuilderConfig] = useState(null);

  const category = CATEGORY_CONFIG[activeCategory];
  const activeOption = useMemo(() => {
    return category.options.find((option) => option.value === selectedByCategory[activeCategory]);
  }, [activeCategory, category.options, selectedByCategory]);

  const handleCategoryChange = (nextKey) => {
    setActiveCategory(nextKey);
    setFeedback('');
    setBuilderConfig(null);
  };

  const handleOptionChange = (value) => {
    setSelectedByCategory((prev) => ({ ...prev, [activeCategory]: value }));
    setFeedback('');
  };

  const handleNext = () => {
    if (!activeOption) return;

    switch (activeOption.type) {
      case 'builder':
        setBuilderConfig({ categoryLabel: category.label, optionLabel: activeOption.label });
        break;
      case 'info':
      default:
        setFeedback(activeOption.message || 'This option is not available yet.');
        break;
    }
  };

  const handleDiscard = () => {
    setSelectedByCategory({ marketing: 'custom', utility: 'custom', authentication: 'otp' });
    setFeedback('Selection reset.');
    setBuilderConfig(null);
  };

  if (builderConfig) {
    return (
      <TemplateBuilder
        categoryLabel={builderConfig.categoryLabel}
        optionLabel={builderConfig.optionLabel}
        onBack={() => setBuilderConfig(null)}
      />
    );
  }

  return (
    <div className="create-template-page">
      <header className="create-template-header">
        <h2>Create Template</h2>
        <p>Select the template category that matches your message. Each category supports different workflows.</p>
      </header>

      <div className="create-template-layout">
        <section className="create-template-panel">
          <h3>Choose a category</h3>
          <p className="panel-subtitle">Pick the template use-case first, then select the message format.</p>
          <div className="category-toggle">
            {CATEGORY_ORDER.map((key) => {
              const item = CATEGORY_CONFIG[key];
              const Icon = item.icon;
              const isActive = activeCategory === key;
              return (
                <button
                  key={key}
                  type="button"
                  className={`category-toggle__btn ${isActive ? 'active' : ''}`}
                  onClick={() => handleCategoryChange(key)}
                >
                  <Icon />
                  <span>{item.label}</span>
                </button>
              );
            })}
          </div>

          <div className="option-list" role="radiogroup" aria-label={`${category.label} options`}>
            {category.options.map((option) => {
              const isSelected = selectedByCategory[activeCategory] === option.value;
              return (
                <label key={option.value} className={`option-card ${isSelected ? 'selected' : ''}`}>
                  <input
                    type="radio"
                    name={`option-${activeCategory}`}
                    value={option.value}
                    checked={isSelected}
                    onChange={() => handleOptionChange(option.value)}
                  />
                  <div className="option-card__content">
                    <h4>{option.label}</h4>
                    <p>{option.description}</p>
                    {option.type === 'info' && <span className="badge">Coming soon</span>}
                  </div>
                </label>
              );
            })}
          </div>
        </section>

        <aside className="create-template-preview">
          <h3>Preview</h3>
          <p className="panel-subtitle">We will walk you through drafting the actual message in the next step.</p>
          <div className="preview-card">
            <img src={category.image} alt={`${category.label} illustration`} />
          </div>
          {feedback && <div className="inline-feedback">{feedback}</div>}
        </aside>
      </div>

      <footer className="create-template-footer">
        <button type="button" className="btn btn-muted" onClick={handleDiscard}>
          Discard
        </button>
        <button type="button" className="btn btn-primary" onClick={handleNext}>
          Next
        </button>
      </footer>
    </div>
  );
};

export default CreateTemplate;
